import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getResidentPortal, requestOTP, verifyOTP, createRequest, createIncident } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '../components/ui/input-otp';
import {
  Shield,
  MapPin,
  Megaphone,
  Pill,
  Package,
  AlertTriangle,
  Phone,
  Camera,
  Loader2,
  CheckCircle,
  XCircle,
  Ambulance,
  Flame,
  Shield as ShieldIcon,
} from 'lucide-react';

export default function ResidentPortal() {
  const { qrCode } = useParams();
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // OTP State
  const [showOTP, setShowOTP] = useState(false);
  const [mobile, setMobile] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');

  // Request State
  const [showRequest, setShowRequest] = useState(false);
  const [requestType, setRequestType] = useState('');
  const [requestUrgency, setRequestUrgency] = useState('medium');
  const [requestNotes, setRequestNotes] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);

  // Emergency State
  const [showEmergency, setShowEmergency] = useState(false);
  const [emergencyType, setEmergencyType] = useState('');
  const [emergencyLocation, setEmergencyLocation] = useState('');
  const [selfieData, setSelfieData] = useState(null);
  const [emergencyLoading, setEmergencyLoading] = useState(false);
  const [emergencySuccess, setEmergencySuccess] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoCoords, setGeoCoords] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);

  useEffect(() => {
    loadPortal();
  }, [qrCode]);

  const loadPortal = async () => {
    try {
      const response = await getResidentPortal(qrCode);
      setPortal(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async () => {
    if (!mobile || mobile.length < 10) {
      setOtpError('Please enter a valid mobile number');
      return;
    }
    setOtpLoading(true);
    setOtpError('');
    try {
      await requestOTP(mobile, portal.household_id);
      setShowOTP(true);
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otpCode.length !== 6) {
      setOtpError('Please enter the 6-digit OTP');
      return;
    }
    setOtpLoading(true);
    setOtpError('');
    try {
      await verifyOTP(mobile, otpCode, portal.household_id);
      setOtpVerified(true);
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!requestType) return;
    setRequestLoading(true);
    try {
      await createRequest({
        household_id: portal.household_id,
        request_type: requestType,
        urgency: requestUrgency,
        notes: requestNotes,
        mobile_verified: mobile,
      });
      setRequestSuccess(true);
      setTimeout(() => {
        setShowRequest(false);
        setRequestSuccess(false);
        resetOTP();
      }, 2000);
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setRequestLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraActive(true);
      }
    } catch (err) {
      setOtpError('Camera access denied. Please allow camera access.');
    }
  };

  const captureSelfie = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      const data = canvas.toDataURL('image/jpeg', 0.7);
      setSelfieData(data);
      stopCamera();
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      setCameraActive(false);
    }
  };

  const getLocation = () => {
    setGeoLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGeoCoords({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setGeoLoading(false);
        },
        () => {
          setGeoLoading(false);
        }
      );
    } else {
      setGeoLoading(false);
    }
  };

  const handleSubmitEmergency = async () => {
    if (!emergencyType || !selfieData) {
      setOtpError('Please complete all required fields');
      return;
    }
    setEmergencyLoading(true);
    try {
      await createIncident({
        barangay_id: portal.household_id ? null : undefined,
        household_id: portal.household_id,
        incident_type: emergencyType,
        location_text: emergencyLocation || portal.address_label,
        location_lat: geoCoords?.lat,
        location_lng: geoCoords?.lng,
        mobile_verified: mobile,
        selfie_data: selfieData,
      });
      setEmergencySuccess(true);
      setTimeout(() => {
        setShowEmergency(false);
        setEmergencySuccess(false);
        resetOTP();
      }, 3000);
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'Failed to submit emergency');
    } finally {
      setEmergencyLoading(false);
    }
  };

  const resetOTP = () => {
    setMobile('');
    setOtpCode('');
    setOtpVerified(false);
    setShowOTP(false);
    setOtpError('');
    setRequestType('');
    setRequestNotes('');
    setEmergencyType('');
    setEmergencyLocation('');
    setSelfieData(null);
    setGeoCoords(null);
    stopCamera();
  };

  const openServiceModal = (type) => {
    resetOTP();
    if (type === 'emergency') {
      setShowEmergency(true);
    } else {
      setRequestType(type);
      setShowRequest(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-16 w-16 mx-auto text-destructive mb-4" />
            <h2 className="text-xl font-bold mb-2">QR Code Invalid</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="resident-portal">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8" />
            <div>
              <h1 className="font-bold font-['Public_Sans']">{portal?.municipality}</h1>
              <p className="text-sm text-primary-foreground/80">{portal?.barangay}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto p-4 space-y-4">
        {/* Address Card */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Registered Address</p>
                <p className="font-medium">{portal?.address_label || 'Address not assigned'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Announcements */}
        {portal?.announcements?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Megaphone className="h-4 w-4" />
                Announcements
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {portal.announcements.map((ann, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-sm">
                  <div className="flex items-center gap-2">
                    {ann.pinned && <Badge variant="secondary" className="text-xs">Pinned</Badge>}
                    <h4 className="font-medium text-sm">{ann.title}</h4>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{ann.body}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Services */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Barangay Services</CardTitle>
            <CardDescription>Request assistance from your barangay</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              className="h-auto py-4 flex-col gap-2"
              onClick={() => openServiceModal('Medicine')}
              data-testid="request-medicine-btn"
            >
              <Pill className="h-6 w-6 text-primary" />
              <span className="text-sm">Request Medicine</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex-col gap-2"
              onClick={() => openServiceModal('Equipment')}
              data-testid="request-equipment-btn"
            >
              <Package className="h-6 w-6 text-primary" />
              <span className="text-sm">Request Equipment</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex-col gap-2"
              onClick={() => openServiceModal('Relief')}
              data-testid="request-relief-btn"
            >
              <Package className="h-6 w-6 text-primary" />
              <span className="text-sm">Request Relief</span>
            </Button>
            <Button
              variant="destructive"
              className="h-auto py-4 flex-col gap-2"
              onClick={() => openServiceModal('emergency')}
              data-testid="emergency-btn"
            >
              <AlertTriangle className="h-6 w-6" />
              <span className="text-sm">Emergency</span>
            </Button>
          </CardContent>
        </Card>

        {/* Hotline */}
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground text-center">
              For emergencies, you may also call:
            </p>
            <p className="text-center font-bold text-lg text-primary mt-1">911</p>
          </CardContent>
        </Card>
      </main>

      {/* Request Dialog */}
      <Dialog open={showRequest} onOpenChange={(open) => { if (!open) resetOTP(); setShowRequest(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request {requestType}</DialogTitle>
            <DialogDescription>
              Please verify your mobile number to submit a request
            </DialogDescription>
          </DialogHeader>

          {requestSuccess ? (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-bold">Request Submitted!</h3>
              <p className="text-muted-foreground">Your barangay will process your request.</p>
            </div>
          ) : !otpVerified ? (
            <div className="space-y-4">
              {otpError && (
                <Alert variant="destructive">
                  <AlertDescription>{otpError}</AlertDescription>
                </Alert>
              )}
              
              {!showOTP ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="mobile">Mobile Number</Label>
                    <Input
                      id="mobile"
                      type="tel"
                      placeholder="09171234567"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      data-testid="mobile-input"
                    />
                  </div>
                  <Button
                    className="w-full"
                    onClick={handleRequestOTP}
                    disabled={otpLoading}
                    data-testid="request-otp-btn"
                  >
                    {otpLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Phone className="h-4 w-4 mr-2" />}
                    Send OTP
                  </Button>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Enter OTP sent to {mobile}</Label>
                    <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode}>
                      <InputOTPGroup className="justify-center w-full">
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <Button
                    className="w-full"
                    onClick={handleVerifyOTP}
                    disabled={otpLoading}
                    data-testid="verify-otp-btn"
                  >
                    {otpLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Verify OTP
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Urgency</Label>
                <Select value={requestUrgency} onValueChange={setRequestUrgency}>
                  <SelectTrigger data-testid="urgency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea
                  placeholder="Describe your request..."
                  value={requestNotes}
                  onChange={(e) => setRequestNotes(e.target.value)}
                  data-testid="request-notes"
                />
              </div>
              <Button
                className="w-full"
                onClick={handleSubmitRequest}
                disabled={requestLoading}
                data-testid="submit-request-btn"
              >
                {requestLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Submit Request
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Emergency Dialog */}
      <Dialog open={showEmergency} onOpenChange={(open) => { if (!open) { resetOTP(); stopCamera(); } setShowEmergency(open); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Emergency Request
            </DialogTitle>
            <DialogDescription>
              For life-threatening emergencies only. Misuse may result in penalties.
            </DialogDescription>
          </DialogHeader>

          {emergencySuccess ? (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-bold">Emergency Reported!</h3>
              <p className="text-muted-foreground">Help is on the way. Stay calm.</p>
            </div>
          ) : !otpVerified ? (
            <div className="space-y-4">
              {otpError && (
                <Alert variant="destructive">
                  <AlertDescription>{otpError}</AlertDescription>
                </Alert>
              )}
              
              {!showOTP ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="emergency-mobile">Mobile Number</Label>
                    <Input
                      id="emergency-mobile"
                      type="tel"
                      placeholder="09171234567"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      data-testid="emergency-mobile-input"
                    />
                  </div>
                  <Button
                    className="w-full"
                    variant="destructive"
                    onClick={handleRequestOTP}
                    disabled={otpLoading}
                    data-testid="emergency-request-otp-btn"
                  >
                    {otpLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Phone className="h-4 w-4 mr-2" />}
                    Send OTP
                  </Button>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Enter OTP sent to {mobile}</Label>
                    <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode}>
                      <InputOTPGroup className="justify-center w-full">
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <Button
                    className="w-full"
                    variant="destructive"
                    onClick={handleVerifyOTP}
                    disabled={otpLoading}
                    data-testid="emergency-verify-otp-btn"
                  >
                    {otpLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Verify OTP
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {otpError && (
                <Alert variant="destructive">
                  <AlertDescription>{otpError}</AlertDescription>
                </Alert>
              )}

              {/* Emergency Type */}
              <div className="space-y-2">
                <Label>Emergency Type *</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    variant={emergencyType === 'Medical' ? 'default' : 'outline'}
                    className="flex-col h-auto py-3"
                    onClick={() => setEmergencyType('Medical')}
                    data-testid="emergency-type-medical"
                  >
                    <Ambulance className="h-5 w-5" />
                    <span className="text-xs mt-1">Medical</span>
                  </Button>
                  <Button
                    type="button"
                    variant={emergencyType === 'Fire' ? 'destructive' : 'outline'}
                    className="flex-col h-auto py-3"
                    onClick={() => setEmergencyType('Fire')}
                    data-testid="emergency-type-fire"
                  >
                    <Flame className="h-5 w-5" />
                    <span className="text-xs mt-1">Fire</span>
                  </Button>
                  <Button
                    type="button"
                    variant={emergencyType === 'Police' ? 'secondary' : 'outline'}
                    className="flex-col h-auto py-3"
                    onClick={() => setEmergencyType('Police')}
                    data-testid="emergency-type-police"
                  >
                    <ShieldIcon className="h-5 w-5" />
                    <span className="text-xs mt-1">Police</span>
                  </Button>
                </div>
              </div>

              {/* Selfie Capture */}
              <div className="space-y-2">
                <Label>Selfie Verification *</Label>
                {!selfieData ? (
                  <div className="border border-dashed border-slate-300 rounded-sm p-4">
                    {cameraActive ? (
                      <div className="space-y-3">
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          className="w-full rounded-sm"
                        />
                        <Button
                          className="w-full"
                          onClick={captureSelfie}
                          data-testid="capture-selfie-btn"
                        >
                          <Camera className="h-4 w-4 mr-2" />
                          Capture
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={startCamera}
                        data-testid="start-camera-btn"
                      >
                        <Camera className="h-4 w-4 mr-2" />
                        Open Camera
                      </Button>
                    )}
                    <canvas ref={canvasRef} className="hidden" />
                  </div>
                ) : (
                  <div className="relative">
                    <img src={selfieData} alt="Selfie" className="w-full rounded-sm" />
                    <Button
                      variant="outline"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => { setSelfieData(null); startCamera(); }}
                    >
                      Retake
                    </Button>
                  </div>
                )}
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label>Location</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter location or use GPS"
                    value={emergencyLocation}
                    onChange={(e) => setEmergencyLocation(e.target.value)}
                    data-testid="emergency-location-input"
                  />
                  <Button
                    variant="outline"
                    onClick={getLocation}
                    disabled={geoLoading}
                    data-testid="get-gps-btn"
                  >
                    {geoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                  </Button>
                </div>
                {geoCoords && (
                  <p className="text-xs text-muted-foreground">
                    GPS: {geoCoords.lat.toFixed(6)}, {geoCoords.lng.toFixed(6)}
                  </p>
                )}
              </div>

              <Button
                className="w-full"
                variant="destructive"
                onClick={handleSubmitEmergency}
                disabled={emergencyLoading || !emergencyType || !selfieData}
                data-testid="submit-emergency-btn"
              >
                {emergencyLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
                Report Emergency
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
